import { MangaDexSource } from "../../sources/MangaDexSource.ts";
import { SourceEngine } from "../SourceEngine.ts";
import { validateProfile } from "../ProfileValidator.ts";
import { resolveUrl } from "../UrlResolver.ts";
import { getSourcePolicy } from "../../network/SourceRegistry.ts";
import type { SourceProfile, Value } from "../types.ts";
import type { YomiriHttpClient } from "../../network/types.ts";
import type { YomiriManga, YomiriChapter, YomiriPage } from "../../types/extension.ts";
import { yomiriDatabase } from "../../storage/YomiriDatabase.ts";

export function MangaDexProfile(): SourceProfile {
  const fields = { item: {} };
  const list = { kind: "json" as const, path: "data", allowEmpty: true, fields };
  return validateProfile({
    schemaVersion: 1,
    id: "mangadex",
    name: "MangaDex",
    baseUrl: "https://api.mangadex.org",
    allowedHosts: [...getSourcePolicy("mangadex").allowedHosts],
    template: "CustomCSS",
    corsOpen: true,
    operations: {
      search: {
        steps: [
          {
            url: "{baseUrl}/manga?title={query}&limit=20&offset={page}&includes[]=cover_art&contentRating[]=safe&contentRating[]=suggestive",
            capture: { total: { path: "total" } },
            extract: list,
          },
        ],
      },
      detail: {
        steps: [
          {
            url: "{baseUrl}/manga/{manga.id}?includes[]=cover_art&includes[]=author&includes[]=artist",
            extract: { kind: "json", path: "data", fields },
          },
        ],
      },
      chapters: {
        steps: [
          {
            url: "{baseUrl}/manga/{manga.id}/feed?limit=100&offset={page}&order[chapter]=desc&includeExternalUrl=0&translatedLanguage[]=es&translatedLanguage[]=es-la",
            extract: list,
          },
        ],
      },
      pages: {
        steps: [
          { url: "{baseUrl}/at-home/server/{chapter.id}", extract: { kind: "json", fields } },
        ],
      },
    },
    catalog: {
      steps: [
        {
          url: "{baseUrl}/manga?limit=20&offset={page}&includes[]=cover_art&order[followedCount]=desc&contentRating[]=safe&contentRating[]=suggestive",
          extract: list,
        },
      ],
    },
    latest: {
      steps: [
        {
            url: "{baseUrl}/manga?limit=20&offset={page}&includes[]=cover_art&order[createdAt]=desc&contentRating[]=safe&contentRating[]=suggestive",
          extract: list,
        },
      ],
    },
  });
}
type ApiObject = { [key: string]: Value };
const record = (value: Value): ApiObject => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Objeto MangaDex inválido.");
  return value;
};
const translated = (value: Value | undefined) => {
  const map = value ? record(value) : {};
  return String(map.es ?? map["es-la"] ?? map.en ?? Object.values(map)[0] ?? "");
};
export class MangaDex extends MangaDexSource {
  readonly supportsNewlyAdded = true;
  private profile: SourceProfile;
  private engine: SourceEngine;
  constructor(
    profile: unknown,
    private http: Pick<YomiriHttpClient, "text">,
  ) {
    super();
    this.profile = validateProfile(profile);
    this.engine = new SourceEngine(profile, http);
  }
  private format(value: Value): YomiriManga {
    const item = record(value),
      attrs = record(item.attributes),
      relations = (item.relationships ?? []) as ApiObject[];
    const rel = (type: string) => relations.find((r) => r.type === type);
    const attr = (type: string, key: string) => {
      const attributes = rel(type)?.attributes;
      return attributes ? record(attributes)[key] : undefined;
    };
    const cover = attr("cover_art", "fileName");
    const id = String(item.id);
    return {
      id,
      sourceId: this.id,
      title: translated(attrs.title),
      description: translated(attrs.description),
      url: `https://mangadex.org/title/${id}`,
      coverUrl: cover
        ? resolveUrl(
            `https://uploads.mangadex.org/covers/${id}/${cover}.512.jpg`,
            this.profile.baseUrl,
            this.profile,
          )
        : undefined,
      status:
        (
          {
            ongoing: "ONGOING",
            completed: "COMPLETED",
            cancelled: "CANCELLED",
            hiatus: "ON_HIATUS",
          } as const
        )[String(attrs.status) as "ongoing"] ?? "UNKNOWN",
      genres: ((attrs.tags ?? []) as ApiObject[]).map((t) => translated(record(t.attributes).name)),
      author:
        typeof attr("author", "name") === "string" ? String(attr("author", "name")) : undefined,
      artist:
        typeof attr("artist", "name") === "string" ? String(attr("artist", "name")) : undefined,
    };
  }
  async getSearchFilters() { return [
    { key: 'status[]', label: 'Estado', options: [ { value: 'ongoing', label: 'En publicación' }, { value: 'completed', label: 'Completado' }, { value: 'hiatus', label: 'En pausa' }, { value: 'cancelled', label: 'Cancelado' } ] },
    { key: 'publicationDemographic[]', label: 'Demografía', options: [ { value: 'shounen', label: 'Shōnen' }, { value: 'shoujo', label: 'Shōjo' }, { value: 'seinen', label: 'Seinen' }, { value: 'josei', label: 'Josei' } ] },
  ]; }
  private async listing(query: string, page: number, kind: "search" | "catalog" | "latest", filters: Record<string, unknown> = {}) {
    if (Object.values(filters).some(Boolean)) {
      const options = await this.getSearchFilters();
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (!value) continue;
        if (!options.some(filter => filter.key === key && filter.options.some(option => option.value === value))) throw new Error('Filtro de MangaDex inválido.');
        params.set(key, String(value));
      }
      const definition = kind === 'search' ? this.profile.operations.search : this.profile[kind] ?? this.profile.operations.search;
      const engine = new SourceEngine({ ...this.profile, operations: { ...this.profile.operations, search: {
        ...definition, steps: [{ ...definition.steps[0], url: `${definition.steps[0].url}&${params}` }, ...definition.steps.slice(1)],
      } } }, this.http);
      const result = await engine.search({ query, page: (page - 1) * 20 });
      const mangas = result.items.map(row => this.format(row.item));
      return { mangas, hasNextPage: mangas.length === 20 };
    }
    const engine =
      kind === "search"
        ? this.engine
        : new SourceEngine(
            {
              ...this.profile,
              operations: {
                ...this.profile.operations,
                search: this.profile[kind] ?? this.profile.operations.search,
              },
            },
            this.http,
          );
    const result = await engine.search({ query, page: (page - 1) * 20 });
    const mangas = result.items.map((row) => this.format(row.item));
    return { mangas, hasNextPage: mangas.length === 20 };
  }
  override search(query: string, page = 1, filters: Record<string, unknown> = {}) {
    return this.listing(query, page, query ? "search" : "latest", filters);
  }
  override getPopular(page = 1) {
    return this.listing("", page, "catalog");
  }
  override getLatest(page = 1) {
    return this.listing("", page, "latest");
  }
  override async getManga(id: string) {
    this.uuid(id);
    const result = await this.engine.detail({
      manga: { id, url: `${this.profile.baseUrl}/manga/${id}` },
    });
    return this.format(result.items[0].item);
  }
  override async getChapters(
    id: string,
    options?: { language?: string; languages?: string[] },
  ): Promise<YomiriChapter[]> {
    this.uuid(id);
    let saved = ["es"];
    if (!options?.languages && !options?.language)
      try {
        saved = (await yomiriDatabase.getSetting<string[]>("source_languages_mangadex", [
          "es",
        ])) ?? ["es"];
      } catch {
        /* Offline/test environments keep Spanish. */
      }
    const languages =
      options?.languages ??
      (options?.language ? [options.language] : saved.length ? saved : ["es"]);
    const translatedLanguages = languages.flatMap((lang) =>
      lang === "es" ? ["es", "es-la"] : [lang],
    );
    const profile = structuredClone(this.profile);
    const endpoint = new URL(
      profile.operations.chapters.steps[0].url
        .replace("{baseUrl}", this.profile.baseUrl)
        .replace("{manga.id}", id)
        .replace("{page}", "0"),
    );
    endpoint.searchParams.delete("translatedLanguage[]");
    if (!languages.includes("all"))
      for (const lang of translatedLanguages)
        endpoint.searchParams.append("translatedLanguage[]", lang);
    const chapters: YomiriChapter[] = [];
    for (let offset = 0; offset < 10000; offset += 100) {
      endpoint.searchParams.set("offset", String(offset));
      profile.operations.chapters.steps[0].url = endpoint.href;
      const result = await new SourceEngine(profile, this.http).chapters({
        manga: { id, url: `${this.profile.baseUrl}/manga/${id}` },
        page: offset,
      });
      for (const row of result.items) {
        const item = record(row.item),
          a = record(item.attributes);
        chapters.push({
          id: String(item.id),
          sourceId: this.id,
          mangaId: id,
          name: `Capítulo ${a.chapter ?? "?"}${a.title ? `: ${a.title}` : ""} [${String(a.translatedLanguage ?? "es").toUpperCase()}]`,
          chapterNumber: Number(a.chapter ?? 0) || 0,
          language: String(a.translatedLanguage ?? "es"),
          pageCount: Number(a.pages ?? 0),
          uploadDate: typeof a.publishAt === "string" ? a.publishAt : undefined,
        });
      }
      if (result.items.length < 100) return chapters;
    }
    throw new Error("El listado de capítulos excedió el límite de paginación.");
  }
  override async getPages(id: string): Promise<YomiriPage[]> {
    this.uuid(id);
    const result = await this.engine.pages({
      chapter: { id, url: `${this.profile.baseUrl}/at-home/server/${id}` },
    });
    const item = record(result.items[0].item),
      chapter = record(item.chapter);
    const saver = Array.isArray(chapter.dataSaver) && chapter.dataSaver.length > 0;
    const files = (saver ? chapter.dataSaver : chapter.data) as Value[];
    if (!Array.isArray(files) || !files.length) throw new Error("MangaDex no devolvió páginas.");
    return files.map((file, index) => ({
      index,
      sourceId: this.id,
      imageUrl: resolveUrl(
        `${item.baseUrl}/${saver ? "data-saver" : "data"}/${chapter.hash}/${file}`,
        this.profile.baseUrl,
        this.profile,
      ),
      ...(saver && Array.isArray(chapter.data) && chapter.data[index] ? {fallbackImageUrl:resolveUrl(
        `${item.baseUrl}/data/${chapter.hash}/${chapter.data[index]}`,this.profile.baseUrl,this.profile,
      )}:{}),
    }));
  }
  private uuid(id: string) {
    if (!/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id))
      throw new Error("ID MangaDex inválido.");
  }
}
